// Direct calls to Cognito IDP / Hosted UI token endpoints. No Amplify, no
// oidc-client-ts — same approach as mobile/services/auth.ts. The IDP endpoint
// allows CORS from any browser origin, so no proxying is required.
//
// Token storage and the in-flight refresh dedupe live in tokens.ts and below;
// PKCE primitives live in pkce.ts.

import { deriveCodeChallenge, generateCodeVerifier, generateState } from './pkce';
import {
  clearTokens,
  hasRefreshToken,
  readIdTokenInMemory,
  readRefreshToken,
  setTokens,
} from './tokens';
import { NotAuthenticatedError } from '../api/types';

const REGION = import.meta.env.VITE_COGNITO_REGION as string;
const USER_POOL_CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID as string;
const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN as string | undefined;
const WEB_ORIGIN = (import.meta.env.VITE_WEB_ORIGIN as string | undefined)
  ?? (typeof window !== 'undefined' ? window.location.origin : '');

const IDP_URL = `https://cognito-idp.${REGION}.amazonaws.com/`;
const REDIRECT_URI = `${WEB_ORIGIN}/auth/callback`;
const LOGOUT_REDIRECT = `${WEB_ORIGIN}/auth/logout`;

// Surfaces a build-time misconfiguration as a useful error in the login UI
// instead of an opaque 4xx from Cognito. Called at the top of every entry
// point that actually talks to Cognito.
function assertConfig(): void {
  if (!REGION) {
    throw new Error('PWA misconfigured: VITE_COGNITO_REGION is missing — rebuild with web/.env present.');
  }
  if (!USER_POOL_CLIENT_ID) {
    throw new Error('PWA misconfigured: VITE_USER_POOL_CLIENT_ID is missing — rebuild with web/.env present.');
  }
}

const VERIFIER_KEY = 'jotly.pkceVerifier';
const STATE_KEY = 'jotly.pkceState';

// ─── Native password sign-in ────────────────────────────────────────────────

export type SignInResult =
  | { kind: 'ok' }
  | { kind: 'new-password-required'; session: string; userIdForSrp: string };

interface InitiateAuthResponse {
  AuthenticationResult?: {
    IdToken: string;
    RefreshToken: string;
    ExpiresIn: number;
  };
  ChallengeName?: string;
  Session?: string;
  ChallengeParameters?: { USER_ID_FOR_SRP?: string };
}

/**
 * A non-2xx response from the Cognito IDP endpoint. Carries the HTTP status
 * and Cognito's `__type` (e.g. "NotAuthorizedException") so callers can tell
 * a genuine auth rejection apart from throttling / 5xx — refreshOnce() must
 * only wipe tokens for the former.
 */
export class CognitoApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly cognitoType: string | null,
  ) {
    super(message);
    this.name = 'CognitoApiError';
  }
}

async function cognitoIdp<T>(target: string, body: unknown): Promise<T> {
  const res = await fetch(IDP_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = '';
    let type: string | null = null;
    try {
      const data = await res.json();
      detail = data?.message || '';
      // Cognito error bodies carry `__type`, sometimes namespace-qualified
      // ("com.amazonaws...#NotAuthorizedException") — keep the bare name.
      const raw = typeof data?.__type === 'string' ? data.__type : null;
      type = raw ? raw.split('#').pop() ?? raw : null;
    } catch { /* noop */ }
    throw new CognitoApiError(
      detail || `Cognito ${target} failed: ${res.status}`,
      res.status,
      type,
    );
  }
  return res.json();
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  assertConfig();
  const data = await cognitoIdp<InitiateAuthResponse>('InitiateAuth', {
    AuthFlow: 'USER_PASSWORD_AUTH',
    ClientId: USER_POOL_CLIENT_ID,
    AuthParameters: { USERNAME: email, PASSWORD: password },
  });

  if (data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
    return {
      kind: 'new-password-required',
      session: data.Session ?? '',
      userIdForSrp: data.ChallengeParameters?.USER_ID_FOR_SRP ?? email,
    };
  }

  if (!data.AuthenticationResult) {
    throw new Error('Unexpected Cognito response.');
  }
  setTokens({
    idToken: data.AuthenticationResult.IdToken,
    refreshToken: data.AuthenticationResult.RefreshToken,
    expiresInSeconds: data.AuthenticationResult.ExpiresIn,
  });
  return { kind: 'ok' };
}

export async function completeNewPassword(args: {
  session: string;
  userIdForSrp: string;
  newPassword: string;
}): Promise<void> {
  assertConfig();
  const data = await cognitoIdp<InitiateAuthResponse>('RespondToAuthChallenge', {
    ChallengeName: 'NEW_PASSWORD_REQUIRED',
    ClientId: USER_POOL_CLIENT_ID,
    Session: args.session,
    ChallengeResponses: {
      USERNAME: args.userIdForSrp,
      NEW_PASSWORD: args.newPassword,
    },
  });
  if (!data.AuthenticationResult) throw new Error('Set-password did not return tokens.');
  setTokens({
    idToken: data.AuthenticationResult.IdToken,
    refreshToken: data.AuthenticationResult.RefreshToken,
    expiresInSeconds: data.AuthenticationResult.ExpiresIn,
  });
}

// ─── Self sign-up ───────────────────────────────────────────────────────────
// Cognito's public SignUp + ConfirmSignUp flow. The user pool emails a 6-digit
// confirmation code; the user pastes it back. PostConfirmation then bootstraps
// the DynamoDB profile and best-effort notifies the admin.

export async function signUp(args: {
  email: string;
  password: string;
  name?: string;
}): Promise<void> {
  assertConfig();
  const attrs: Array<{ Name: string; Value: string }> = [
    { Name: 'email', Value: args.email },
  ];
  if (args.name && args.name.trim()) {
    attrs.push({ Name: 'name', Value: args.name.trim() });
  }
  await cognitoIdp('SignUp', {
    ClientId: USER_POOL_CLIENT_ID,
    Username: args.email,
    Password: args.password,
    UserAttributes: attrs,
  });
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  assertConfig();
  await cognitoIdp('ConfirmSignUp', {
    ClientId: USER_POOL_CLIENT_ID,
    Username: email,
    ConfirmationCode: code,
  });
}

export async function resendConfirmationCode(email: string): Promise<void> {
  assertConfig();
  await cognitoIdp('ResendConfirmationCode', {
    ClientId: USER_POOL_CLIENT_ID,
    Username: email,
  });
}

// ─── Forgot password ────────────────────────────────────────────────────────
// PreventUserExistenceErrors=ENABLED on the client means ForgotPassword
// returns success even for unknown emails (no enumeration leak). The
// CustomMessage trigger rewrites the email body to a link into ResetPasswordScreen
// which calls confirmForgotPassword below.

export async function forgotPassword(email: string): Promise<void> {
  assertConfig();
  await cognitoIdp('ForgotPassword', {
    ClientId: USER_POOL_CLIENT_ID,
    Username: email,
  });
}

export async function confirmForgotPassword(args: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<void> {
  assertConfig();
  await cognitoIdp('ConfirmForgotPassword', {
    ClientId: USER_POOL_CLIENT_ID,
    Username: args.email,
    ConfirmationCode: args.code,
    Password: args.newPassword,
  });
}

// ─── Refresh, with in-flight dedupe ─────────────────────────────────────────
// Multiple parallel `getIdToken()` callers must not each fire a refresh.

let inflightRefresh: Promise<string | null> | null = null;

// Cognito error types that mean "this refresh token will never work again" —
// only these justify wiping stored tokens and bouncing the user to login.
// Everything else (network failure, 5xx, throttling) is transient: keep the
// tokens, return null this once, and let the next call retry.
const AUTH_REJECTION_TYPES = new Set([
  'NotAuthorizedException',
  'UserNotFoundException',
  'PasswordResetRequiredException',
  'UserNotConfirmedException',
]);

function isAuthRejection(e: unknown): boolean {
  return (
    e instanceof CognitoApiError &&
    e.status >= 400 &&
    e.status < 500 &&
    e.cognitoType !== null &&
    AUTH_REJECTION_TYPES.has(e.cognitoType)
  );
}

async function refreshOnce(): Promise<string | null> {
  const refresh = readRefreshToken();
  if (!refresh) return null;

  try {
    const data = await cognitoIdp<InitiateAuthResponse>('InitiateAuth', {
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      ClientId: USER_POOL_CLIENT_ID,
      AuthParameters: { REFRESH_TOKEN: refresh },
    });
    if (!data.AuthenticationResult) {
      // A definitive 2xx from Cognito with no tokens — the refresh token is
      // spent. Clear so the app routes to login rather than looping.
      clearTokens();
      return null;
    }
    setTokens({
      idToken: data.AuthenticationResult.IdToken,
      // Refresh token doesn't always rotate — only update if provided.
      refreshToken: data.AuthenticationResult.RefreshToken,
      expiresInSeconds: data.AuthenticationResult.ExpiresIn,
    });
    return data.AuthenticationResult.IdToken;
  } catch (e) {
    if (isAuthRejection(e)) {
      // Genuine rejection (revoked/expired refresh token, deleted user) —
      // clearTokens() also notifies onAuthInvalidated, routing to login.
      clearTokens();
      return null;
    }
    // Transient (offline, 5xx, throttled): leave the refresh token intact so
    // the session survives — callers see a one-off null and surface a calm
    // "couldn't reach the server" instead of signing the user out.
    return null;
  }
}

export async function getIdToken(): Promise<string | null> {
  const { token, expMs } = readIdTokenInMemory();
  // Refresh proactively when within 60s of expiry.
  const stale = !token || Date.now() > expMs - 60_000;
  if (!stale) return token;

  if (!inflightRefresh) {
    inflightRefresh = refreshOnce().finally(() => { inflightRefresh = null; });
  }
  return inflightRefresh;
}

export function isAuthenticated(): boolean {
  return hasRefreshToken() || !!readIdTokenInMemory().token;
}

/**
 * Best-effort claims decoded from the in-memory ID token's payload.
 * Display-only — no verification needed client-side (the backend authoriser
 * does the real check). Returns nulls before the first refresh resolves or
 * if the token is absent.
 */
export function currentUserClaims(): { sub: string | null; email: string | null; name: string | null } {
  const { token } = readIdTokenInMemory();
  if (!token) return { sub: null, email: null, name: null };
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json) as { sub?: unknown; email?: unknown; name?: unknown };
    return {
      sub: typeof claims.sub === 'string' ? claims.sub : null,
      email: typeof claims.email === 'string' ? claims.email : null,
      name: typeof claims.name === 'string' ? claims.name : null,
    };
  } catch {
    return { sub: null, email: null, name: null };
  }
}

export function currentUserEmail(): string | null {
  return currentUserClaims().email;
}

// ─── Hosted UI / OAuth code flow ────────────────────────────────────────────

type Provider = 'COGNITO' | 'Google' | 'Facebook' | 'SignInWithApple';

export async function startHostedUiFlow(provider?: Provider): Promise<void> {
  assertConfig();
  if (!COGNITO_DOMAIN) {
    throw new Error('VITE_COGNITO_DOMAIN is not configured.');
  }
  const verifier = generateCodeVerifier();
  const challenge = await deriveCodeChallenge(verifier);
  const state = generateState();
  // sessionStorage scopes to this tab — exactly what we want for the
  // outstanding-redirect handshake.
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: USER_POOL_CLIENT_ID,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: REDIRECT_URI,
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  if (provider) params.set('identity_provider', provider);
  window.location.assign(`https://${COGNITO_DOMAIN}/oauth2/authorize?${params}`);
}

export async function exchangeOAuthCode(code: string, returnedState: string): Promise<void> {
  assertConfig();
  if (!COGNITO_DOMAIN) throw new Error('VITE_COGNITO_DOMAIN is not configured.');

  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  if (!expectedState || expectedState !== returnedState) {
    throw new Error('OAuth state mismatch — possible CSRF, login aborted.');
  }
  if (!verifier) {
    throw new Error('Missing PKCE verifier — try signing in again.');
  }

  const form = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: USER_POOL_CLIENT_ID,
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: verifier,
  });

  const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: form.toString(),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`Token exchange failed (${res.status}): ${detail}`);
  }
  const data: {
    id_token: string;
    refresh_token: string;
    expires_in: number;
  } = await res.json();

  setTokens({
    idToken: data.id_token,
    refreshToken: data.refresh_token,
    expiresInSeconds: data.expires_in,
  });
}

export function signOut(): void {
  clearTokens();
  if (COGNITO_DOMAIN) {
    const params = new URLSearchParams({
      client_id: USER_POOL_CLIENT_ID,
      logout_uri: LOGOUT_REDIRECT,
    });
    window.location.assign(`https://${COGNITO_DOMAIN}/logout?${params}`);
  }
}

// Force callers to import NotAuthenticatedError from one place.
export { NotAuthenticatedError };
